import React, { useState } from 'react';
import { SectionCard, Field, TextInput, Select, ChipToggleGroup, ChipInput, SaveRow, palette } from './ui';
import { JOB_TYPES, SHIFTS, SALARY_PERIODS } from './constants';

export default function PreferencesCard({ isDarkMode, profile, onSave }) {
  const c = palette(isDarkMode);
  const isBlueCollar = profile.work_category === 'blue_collar';
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const startEdit = () => {
    setForm({
      job_types: profile.job_types || [],
      shift_preference: profile.shift_preference || 'any',
      preferred_locations: profile.preferred_locations || [],
      expected_salary: profile.expected_salary || '',
      salary_period: profile.salary_period || (isBlueCollar ? 'monthly' : 'yearly'),
      notice_period_days: profile.notice_period_days ?? '',
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    const ok = await onSave({
      ...form,
      expected_salary: form.expected_salary === '' ? null : Number(form.expected_salary),
      notice_period_days: form.notice_period_days === '' ? null : Number(form.notice_period_days),
    });
    setSaving(false);
    if (ok) setEditing(false);
  };

  const label = (list, value) => (list.find(o => o.value === value) || {}).label;
  const salaryText = profile.expected_salary
    ? `₹${Number(profile.expected_salary).toLocaleString('en-IN')} ${label(SALARY_PERIODS, profile.salary_period) || ''}`.trim()
    : null;

  const rows = [
    ['Job type', (profile.job_types || []).map(v => label(JOB_TYPES, v)).filter(Boolean).join(', ')],
    ['Shift', label(SHIFTS, profile.shift_preference)],
    ['Preferred locations', (profile.preferred_locations || []).join(', ')],
    ['Expected pay', salaryText],
    ['Notice period', profile.notice_period_days != null ? `${profile.notice_period_days} days` : null],
  ].filter(([, v]) => v);

  return (
    <SectionCard
      id="preferences"
      title="Career preferences"
      subtitle="What kind of work you want — employers match on this"
      isDarkMode={isDarkMode}
      onEdit={startEdit}
      editing={editing}
    >
      {!editing ? (
        rows.length === 0 ? (
          <p className={`text-sm italic ${c.secondaryText}`}>Tell us your preferred job type, shift, locations and expected pay.</p>
        ) : (
          <dl className="space-y-2">
            {rows.map(([l, v]) => (
              <div key={l} className="flex flex-col sm:flex-row sm:gap-4">
                <dt className={`text-sm w-40 shrink-0 ${c.secondaryText}`}>{l}</dt>
                <dd className={`text-sm ${c.text}`}>{v}</dd>
              </div>
            ))}
          </dl>
        )
      ) : (
        <div className="space-y-4">
          <Field label="Job type">
            <ChipToggleGroup
              isDarkMode={isDarkMode}
              options={JOB_TYPES}
              values={form.job_types}
              onChange={(job_types) => setForm(f => ({ ...f, job_types }))}
            />
          </Field>
          <Field label="Preferred shift">
            <ChipToggleGroup
              isDarkMode={isDarkMode}
              options={SHIFTS}
              values={[form.shift_preference]}
              onChange={(vals) => setForm(f => ({ ...f, shift_preference: vals[vals.length - 1] || 'any' }))}
            />
          </Field>
          <Field label="Preferred locations">
            <ChipInput
              isDarkMode={isDarkMode}
              values={form.preferred_locations}
              onChange={(preferred_locations) => setForm(f => ({ ...f, preferred_locations }))}
              placeholder="Type a city and press Enter"
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Expected pay (₹)">
              <TextInput
                isDarkMode={isDarkMode}
                type="number"
                min="0"
                value={form.expected_salary}
                onChange={e => setForm(f => ({ ...f, expected_salary: e.target.value }))}
                placeholder="Amount"
              />
            </Field>
            <Field label="Pay period">
              <Select
                isDarkMode={isDarkMode}
                value={form.salary_period}
                onChange={e => setForm(f => ({ ...f, salary_period: e.target.value }))}
                options={SALARY_PERIODS}
              />
            </Field>
          </div>
          {!isBlueCollar && (
            <Field label="Notice period (days)">
              <TextInput
                isDarkMode={isDarkMode}
                type="number"
                min="0"
                value={form.notice_period_days}
                onChange={e => setForm(f => ({ ...f, notice_period_days: e.target.value }))}
                placeholder="e.g. 30"
              />
            </Field>
          )}
          <SaveRow isDarkMode={isDarkMode} onSave={save} onCancel={() => setEditing(false)} saving={saving} />
        </div>
      )}
    </SectionCard>
  );
}
